import React, { useState } from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type ContactPerson = {
  firstName: string;
  lastName: string;
  title: string;
  companyPhone: string;
  mobilePhone: string;
  email: string;
};

type ContactPersons = {
  primary: ContactPerson;
  billing: ContactPerson;
  perks: ContactPerson;
  hr: ContactPerson;
};

type ContactPersonsSectionProps = {
  contactPersons: ContactPersons;
  onChange: ({
    personType,
    field,
    value,
  }: {
    personType: keyof ContactPersons;
    field: keyof ContactPerson;
    value: string;
  }) => void;
  errors?: Record<string, string>;
};

const ContactPersonsSection = ({
  contactPersons,
  onChange,
  errors = {},
}: ContactPersonsSectionProps) => {
  const [sameAsPrimary, setSameAsPrimary] = useState<{
    billing: boolean;
    perks: boolean;
    hr: boolean;
  }>({
    billing: false,
    perks: false,
    hr: false,
  });

  const handleChange =
    (personType: keyof ContactPersons) =>
    (field: keyof ContactPerson) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ personType, field, value: e.target.value });
    };

  const handleSameAsPrimaryChange =
    (personType: keyof Omit<ContactPersons, "primary">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;

      setSameAsPrimary((prev) => ({
        ...prev,
        [personType]: isChecked,
      }));

      if (isChecked) {
        // Copy all fields from primary contact to this contact
        for (const field of Object.keys(contactPersons.primary) as Array<
          keyof ContactPerson
        >) {
          onChange({
            personType,
            field,
            value: contactPersons.primary[field],
          });
        }
      }
    };

  const renderContactPersonFields = (
    personType: keyof ContactPersons,
    label: string,
    tooltipPrefix: string,
    description?: string,
    isRequired = false
  ) => {
    const person = contactPersons[personType];
    const errorPrefix = personType === "primary" ? "" : personType;
    const isPrimary = personType === "primary";
    const isDisabled =
      !isPrimary &&
      sameAsPrimary[personType as keyof Omit<ContactPersons, "primary">];

    return (
      <Accordion defaultExpanded={personType === "primary"}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="flex flex-col">
            <Typography variant="subtitle1">{label} Contact Person</Typography>
            {description && (
              <Typography
                variant="body2"
                color="text.secondary"
                className="mt-1"
              >
                {description}
              </Typography>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {!isPrimary && (
            <div className="mb-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      sameAsPrimary[
                        personType as keyof Omit<ContactPersons, "primary">
                      ]
                    }
                    onChange={handleSameAsPrimaryChange(
                      personType as keyof Omit<ContactPersons, "primary">
                    )}
                  />
                }
                label="Same as Primary Contact"
              />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 pt-2">
            <InputField
              id={`${personType}-first-name`}
              name={`${personType}FirstName`}
              label="First Name"
              value={person.firstName}
              onChange={handleChange(personType)("firstName")}
              error={!!errors[`${errorPrefix}FirstName`]}
              helperText={errors[`${errorPrefix}FirstName`] || ""}
              required={isRequired}
              tooltipText={`${tooltipPrefix} contact person's first name`}
              disabled={isDisabled}
            />

            <InputField
              id={`${personType}-last-name`}
              name={`${personType}LastName`}
              label="Last Name"
              value={person.lastName}
              onChange={handleChange(personType)("lastName")}
              error={!!errors[`${errorPrefix}LastName`]}
              helperText={errors[`${errorPrefix}LastName`] || ""}
              required={isRequired}
              tooltipText={`${tooltipPrefix} contact person's last name`}
              disabled={isDisabled}
            />

            <InputField
              id={`${personType}-title`}
              name={`${personType}Title`}
              label="Job Title"
              value={person.title}
              onChange={handleChange(personType)("title")}
              error={!!errors[`${errorPrefix}Title`]}
              helperText={errors[`${errorPrefix}Title`] || ""}
              tooltipText={`${tooltipPrefix} contact person's job title or position`}
              disabled={isDisabled}
            />

            <InputField
              id={`${personType}-email`}
              name={`${personType}Email`}
              label="Email"
              value={person.email}
              onChange={handleChange(personType)("email")}
              error={!!errors[`${errorPrefix}Email`]}
              helperText={errors[`${errorPrefix}Email`] || ""}
              required={isRequired}
              tooltipText={`${tooltipPrefix} contact person's email address`}
              disabled={isDisabled}
            />

            <InputField
              id={`${personType}-company-phone`}
              name={`${personType}CompanyPhone`}
              label="Company Phone"
              value={person.companyPhone}
              onChange={handleChange(personType)("companyPhone")}
              error={!!errors[`${errorPrefix}CompanyPhone`]}
              helperText={errors[`${errorPrefix}CompanyPhone`] || ""}
              tooltipText={`${tooltipPrefix} contact person's office or work phone number`}
              disabled={isDisabled}
            />

            <InputField
              id={`${personType}-mobile-phone`}
              name={`${personType}MobilePhone`}
              label="Mobile Phone"
              value={person.mobilePhone}
              onChange={handleChange(personType)("mobilePhone")}
              error={!!errors[`${errorPrefix}MobilePhone`]}
              helperText={errors[`${errorPrefix}MobilePhone`] || ""}
              tooltipText={`${tooltipPrefix} contact person's mobile phone number`}
              disabled={isDisabled}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <FormSection
      title="Contact Persons"
      tooltipText="Enter information for different contact roles within the company"
    >
      <div className="space-y-4">
        {renderContactPersonFields(
          "billing",
          "Directory Listing",
          "Enter the directory listing",
          "The person who will be listed in the website and Chamber Perks App member directories."
        )}
        {renderContactPersonFields(
          "billing",
          "Communications",
          "Enter the communications",
          "The person responsible for handling communications with the chamber."
        )}
        {renderContactPersonFields(
          "billing",
          "Billing",
          "Enter the billing",
          "The person responsible for handling invoices and payments."
        )}
        {renderContactPersonFields(
          "perks",
          "Chamber Perks App",
          "Enter the Chamber Perks App",
          "The contact who will receive Chamber Perks App member dashboard credentials for reviewing company profile and creating perks."
        )}
        {renderContactPersonFields(
          "hr",
          "HR",
          "Enter the HR",
          "The contact responsible for sharing chamber benefits with employees."
        )}
      </div>
    </FormSection>
  );
};

export default ContactPersonsSection;
